/**
 * Packs product-func-grid items into two newspaper columns by measured height.
 */

import {
  addMediaQueryListener,
  isMobileBreakpoint,
  mediaQueryLarge
} from '@theme/utilities'

const ROOT_SELECTOR = '[data-product-func-grid]'
const LIST_SELECTOR = '.product-func-grid__list'
const ITEM_SELECTOR = '.product-func-grid__item'
const COLUMN_SELECTOR = '.product-func-grid__column'
const PACK_KEY = 'gridPackKey'

/**
 * @param {number} count
 * @returns {{ left: number[], right: number[] }}
 */
export function splitHalf(count) {
  const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  const mid = Math.ceil(safeCount / 2)
  const left = []
  const right = []

  for (let index = 0; index < safeCount; index++) {
    if (index < mid) left.push(index)
    else right.push(index)
  }

  return { left, right }
}

/**
 * Assign each item (source order) to the column with the smaller next height.
 * Ties go left.
 *
 * @param {number[]} heights
 * @param {number} gap
 * @returns {{ left: number[], right: number[] }}
 */
export function packByShorterColumn(heights, gap) {
  const left = []
  const right = []
  let leftHeight = 0
  let rightHeight = 0
  const safeGap = Number.isFinite(gap) && gap > 0 ? gap : 0

  for (const [index, rawHeight] of heights.entries()) {
    const height = Number.isFinite(rawHeight) ? rawHeight : 0
    const nextLeft = left.length === 0 ? height : leftHeight + safeGap + height
    const nextRight =
      right.length === 0 ? height : rightHeight + safeGap + height

    if (nextLeft <= nextRight) {
      left.push(index)
      leftHeight = nextLeft
    } else {
      right.push(index)
      rightHeight = nextRight
    }
  }

  return { left, right }
}

/**
 * @param {{ left: number[], right: number[] }} assignment
 * @returns {string}
 */
export function assignmentKey(assignment) {
  return `${assignment.left.join(',')}|${assignment.right.join(',')}`
}

/**
 * @param {string} value
 * @returns {number}
 */
export function parseGapPx(value) {
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** @type {WeakSet<HTMLElement>} */
const ready = new WeakSet()

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement[]}
 */
const collectItems = (root) => {
  const items = [...root.querySelectorAll(ITEM_SELECTOR)].filter(
    (node) => node instanceof HTMLElement
  )

  items.sort((left, right) => {
    const leftIndex = Number.parseInt(
      left.getAttribute('data-grid-index') || '0',
      10
    )
    const rightIndex = Number.parseInt(
      right.getAttribute('data-grid-index') || '0',
      10
    )
    return leftIndex - rightIndex
  })

  return items
}

/**
 * @param {HTMLElement} list
 * @returns {HTMLElement[]}
 */
const ensureColumns = (list) => {
  const columns = [...list.querySelectorAll(COLUMN_SELECTOR)].filter(
    (node) => node instanceof HTMLElement
  )

  while (columns.length < 2) {
    const column = document.createElement('div')
    column.className = 'product-func-grid__column'
    list.appendChild(column)
    columns.push(column)
  }

  return columns
}

/**
 * @param {HTMLElement[]} columns
 * @param {HTMLElement[]} items
 * @param {{ left: number[], right: number[] }} assignment
 */
const applyAssignment = (columns, items, assignment) => {
  const [leftColumn, rightColumn] = columns
  if (!leftColumn || !rightColumn) return

  for (const index of assignment.left) {
    const item = items[index]
    if (item) leftColumn.appendChild(item)
  }

  for (const index of assignment.right) {
    const item = items[index]
    if (item) rightColumn.appendChild(item)
  }

  if (assignment.right.length === 0) rightColumn.remove()
}

/**
 * @param {HTMLElement} root
 */
export function packRoot(root) {
  const list = root.querySelector(LIST_SELECTOR)
  if (!(list instanceof HTMLElement)) return

  const items = collectItems(root)
  if (items.length === 0) return

  const existingColumn = list.querySelector(COLUMN_SELECTOR)
  const gap =
    existingColumn instanceof HTMLElement
      ? parseGapPx(getComputedStyle(existingColumn).gap)
      : 0

  const assignment = isMobileBreakpoint()
    ? splitHalf(items.length)
    : packByShorterColumn(
        items.map((item) => item.offsetHeight),
        gap
      )

  const nextKey = assignmentKey(assignment)
  if (root.dataset[PACK_KEY] === nextKey) return

  root.dataset[PACK_KEY] = nextKey
  applyAssignment(ensureColumns(list), items, assignment)
}

/**
 * @param {HTMLElement} root
 */
const initRoot = (root) => {
  if (ready.has(root)) return
  ready.add(root)

  const pack = () => packRoot(root)
  pack()

  const fonts = document.fonts
  if (fonts?.ready) {
    fonts.ready.then(pack).catch(() => {})
  }

  const observer = new ResizeObserver(() => {
    window.requestAnimationFrame(pack)
  })
  observer.observe(root)

  addMediaQueryListener(mediaQueryLarge, pack)
}

/**
 * @param {ParentNode} [scope]
 */
export function bootProductFuncGrids(scope = document) {
  for (const node of scope.querySelectorAll(ROOT_SELECTOR)) {
    if (node instanceof HTMLElement) initRoot(node)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootProductFuncGrids())
} else {
  bootProductFuncGrids()
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target
  if (section instanceof HTMLElement) bootProductFuncGrids(section)
})
